import { thumb } from "../lib/site";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  MousePointerClick,
  MessageCircle,
  MapPin,
  FileText,
  Gauge,
  Smartphone,
  Layers,
  PenTool,
  Search,
  Target,
} from "lucide-react";
import SEO from "../components/SEO";
import { faqSchema } from "../data/schema";
import HeroCanvas from "../components/HeroCanvas";
import Marquee from "../components/Marquee";
import LeadForm from "../components/LeadForm";
import Testimonials from "../components/Testimonials";
import HomeFAQ, { FAQ_ITEMS } from "../components/HomeFAQ";
import ServiceSelector from "../components/ServiceSelector";
import GrowthScore from "../components/GrowthScore";
import FomoBand from "../components/FomoBand";
import { Reveal, Stagger, Item, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { SERVICES, PROJECTS, INDUSTRIES } from "../data/content";
import { waLink, CONTEXT_MESSAGES, WA_NUMBER_DISPLAY } from "../lib/whatsapp";
import { track } from "../lib/analytics";

const PROBLEMS = [
  "Looks outdated the moment a customer opens it",
  "No SEO strategy — invisible on Google",
  "Loads slowly on mobile connections",
  "Broken or cramped on phones",
  "No clear call-to-action anywhere",
  "Visitors browse, then leave without enquiring",
  "No landing pages for campaigns",
  "No lead capture beyond a buried contact page",
  "No way to follow up with interested visitors",
  "Nothing new published since launch",
];

const PILLARS = [
  { icon: PenTool, h: "Premium Design", p: "Make a first impression strong enough to win the shortlist." },
  { icon: Search, h: "SEO Foundation", p: "Built so search engines understand — and rank — your business." },
  { icon: Target, h: "Lead Generation", p: "Forms, WhatsApp CTAs and conversion-focused pages throughout." },
  { icon: Smartphone, h: "Mobile First", p: "Designed for how your customers actually browse." },
  { icon: Layers, h: "Built to Scale", p: "Add pages, blogs and SEO content as your business grows." },
];

const STEPS = [
  { n: "01", t: "Tell Me About Your Business", d: "Share what you do and who you want to reach. A short conversation is enough to begin." },
  { n: "02", t: "Website Strategy", d: "We plan the pages, customer journey and lead-generation structure before any design begins." },
  { n: "03", t: "Design & Build", d: "A premium, fast, responsive website — built with SEO and conversion baked in, not bolted on." },
  { n: "04", t: "Go Live & Grow", d: "Launch, then optionally continue with monthly SEO, content and growth improvements." },
];

const LEAD_FEATURES = [
  { icon: FileText, h: "Smart Contact Forms", p: "Capture business requirements with the right questions, not just name and email." },
  { icon: MessageCircle, h: "WhatsApp Integration", p: "Instant communication on the channel your customers already live in." },
  { icon: MapPin, h: "Location Landing Pages", p: "Target customers searching in specific cities and areas." },
  { icon: Search, h: "SEO Blog", p: "Articles that generate compounding organic traffic over time." },
  { icon: MousePointerClick, h: "Clear CTAs", p: "Every page guides visitors toward one thing: enquiring." },
  { icon: Gauge, h: "Conversion Optimization", p: "Continuous improvement in how visitors become leads." },
];

export default function Home() {
  const schema = [faqSchema(FAQ_ITEMS)];

  return (
    <main>
      <SEO
        title="Grotech Web | SEO & Lead Generation Website Development"
        description="Fast, premium, SEO-friendly websites that help businesses attract customers and generate enquiries — at an affordable start, with optional monthly growth."
        path="/"
        schema={schema}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-28" data-testid="hero-section">
        <HeroCanvas />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3"
            >
              <span className="h-2 w-2 rounded-full bg-ember" />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/60">
                Website & Digital Growth Partner
              </span>
            </motion.div>

            <h1 className="mt-7 font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl">
              <MaskedLine delay={0.15}>We Build Websites That Bring</MaskedLine>
              <MaskedLine delay={0.3}>
                You <span className="text-ember">Leads & Orders.</span>
              </MaskedLine>
            </h1>

            <p
              style={{ animationDelay: "0.55s" }}
              className="fade-up-in mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg"
            >
              Grotech Web builds professional, fast and conversion-focused websites for businesses in
              India, the USA and Dubai — website development, SEO, Shopify stores, AI chatbots and AI
              automation designed to generate enquiries, leads and orders. Websites from ₹25,000,
              $999 or AED 3,499.
            </p>

            <div
              style={{ animationDelay: "0.7s" }}
              className="fade-up-in mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-primary-cta"
                onClick={() => { track("cta_click", { location: "hero", cta: "free_growth_plan" }); openLeadPopup(); }}
                className="group flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover"
              >
                Get My Free Website Growth Plan
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={waLink(CONTEXT_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-secondary-cta"
                onClick={() => track("whatsapp_click", { location: "hero" })}
                className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
              >
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </div>

            <div
              style={{ animationDelay: "0.9s" }}
              className="fade-up-in mt-12 flex flex-wrap gap-x-8 gap-y-3"
              data-testid="hero-trust-row"
            >
              {["Premium Website", "Affordable Start", "Built to Grow"].map((t) => (
                <span key={t} className="flex items-center gap-2 text-sm text-white/50">
                  <BadgeCheck className="h-4 w-4 text-ember" /> {t}
                </span>
              ))}
            </div>
          </div>

          <div
            style={{ animationDelay: "0.5s" }}
            className="fade-up-in lg:col-span-5"
          >
            <div
              className="rounded-3xl border border-white/10 bg-ink/80 p-6 backdrop-blur-md sm:p-8 ember-glow"
              data-testid="hero-lead-form-card"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">
                Free Website Plan
              </p>
              <h2 className="mt-2 font-display text-xl font-bold tracking-tight">
                Tell me about your business
              </h2>
              <p className="mt-1 text-xs text-white/40">
                Takes 30 seconds. Delivered straight to WhatsApp.
              </p>
              <div className="mt-5">
                <LeadForm
                  source="hero"
                  testIdPrefix="hero-form"
                  onSubmitted={(form, waUrl) => {
                    window.open(waUrl, "_blank", "noopener");
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      <ServiceSelector />

      {/* 01 THE PROBLEM */}
      <section className="py-20 lg:py-32" data-testid="problem-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="01" label="The Problem" />
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Most Business Websites <span className="text-white/35">Don't Bring Business.</span>
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-5">
            {PROBLEMS.map((p, i) => (
              <Item key={i} className="bg-obsidian p-6">
                <span className="font-mono text-[10px] tracking-[0.2em] text-ember/70">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{p}</p>
              </Item>
            ))}
          </Stagger>
          <Reveal delay={0.15}>
            <p className="mt-16 max-w-2xl font-display text-2xl font-bold tracking-tight text-ember sm:text-3xl">
              Your Website Can Do More.
            </p>
            <p className="mt-3 max-w-xl text-white/50">
              A website should be your hardest-working salesperson — visible on Google, fast on any
              phone, and built to turn visitors into enquiries.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 02 THE SOLUTION */}
      <section className="bg-bone py-20 text-obsidian lg:py-32" data-testid="solution-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="02" label="The Solution" dark />
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              A Website Built Around Growth.
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-6">
            {PILLARS.map((pillar, i) => (
              <Item
                key={pillar.h}
                className={`group rounded-2xl border border-obsidian/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-obsidian/5 ${
                  i < 2 ? "lg:col-span-3" : "lg:col-span-2"
                }`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-obsidian text-ember transition-colors group-hover:bg-ember group-hover:text-white">
                  <pillar.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{pillar.h}</h3>
                <p className="mt-2 text-sm leading-relaxed text-obsidian/60">{pillar.p}</p>
              </Item>
            ))}
            <Item className="flex flex-col justify-between rounded-2xl bg-obsidian p-7 text-white lg:col-span-2">
              <p className="font-display text-xl font-bold leading-snug tracking-tight">
                Don't just get a website. Get a website that works to bring you business.
              </p>
              <button
                data-testid="solution-cta"
                onClick={openLeadPopup}
                className="mt-6 flex w-fit items-center gap-2 rounded-full bg-ember px-5 py-3 font-display text-xs font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
              >
                Start Here <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </Item>
          </Stagger>
        </div>
      </section>

      {/* 03 SERVICES */}
      <section id="services" className="py-20 lg:py-32" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Chapter index="03" label="What I Build" />
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
                  Websites Engineered <br className="hidden sm:block" />
                  <span className="text-white/35">for Enquiries.</span>
                </h2>
              </Reveal>
            </div>
          </div>
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Item key={s.slug}>
                <Link
                  to={`/${s.slug}/`}
                  data-testid={`service-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-white/10 bg-ink p-7 transition-all duration-300 hover:-translate-y-1 hover:border-ember/40"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 text-ember">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-white/20 transition-all group-hover:text-ember" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-white/50">{s.short}</p>
                  <span className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em] text-ember/80">
                    Explore Service
                  </span>
                </Link>
              </Item>
            ))}
            <Item>
              <button
                data-testid="services-custom-plan-card"
                onClick={openLeadPopup}
                className="flex h-full w-full flex-col justify-between rounded-2xl bg-ember p-7 text-left transition-transform duration-300 hover:-translate-y-1"
              >
                <h3 className="font-display text-2xl font-bold leading-tight tracking-tight">
                  Not sure what you need yet?
                </h3>
                <div>
                  <p className="mt-2 text-sm text-white/80">
                    Tell me about your business and I'll recommend the right starting point.
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-display text-xs font-bold uppercase tracking-wider text-obsidian">
                    Get a Free Plan <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </button>
            </Item>
          </Stagger>
        </div>
      </section>

      {/* 04 PROCESS */}
      <section id="process" className="border-y border-line bg-ink py-20 lg:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="04" label="How It Works" />
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Four Steps. <span className="text-white/35">Zero Confusion.</span>
            </h2>
          </Reveal>
          <div className="mt-14">
            {STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.06}>
                <div className="group grid gap-4 border-t border-white/10 py-10 transition-colors sm:grid-cols-12 sm:items-center">
                  <span className="font-mono text-sm tracking-[0.2em] text-ember sm:col-span-2">
                    {step.n}
                  </span>
                  <h3 className="font-display text-2xl font-bold tracking-tight sm:col-span-5 sm:text-3xl">
                    {step.t}
                  </h3>
                  <p className="text-sm leading-relaxed text-white/50 sm:col-span-5">{step.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <button
              data-testid="process-cta"
              onClick={() => { track("cta_click", { location: "process" }); openLeadPopup(); }}
              className="group mt-8 flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
            >
              Discuss My Business
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>

      <GrowthScore />

      {/* 05 GROWTH MODEL */}
      <section id="growth" className="bg-bone py-20 text-obsidian lg:py-32" data-testid="growth-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="05" label="The Model" dark />
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Start Affordable. <br /> Grow When You're Ready.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-2xl leading-relaxed text-obsidian/60">
              You don't always need to spend a huge amount upfront to get a professional website.
              Start with a strong, premium foundation — then, when you want more traffic and more
              leads, an optional monthly growth plan keeps building on it.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-2">
            <Reveal className="h-full">
              <div className="flex h-full flex-col rounded-3xl border border-obsidian/10 bg-white p-8 sm:p-10">
                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">Step One</span>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  Build Once.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-obsidian/60">
                  A premium, fast, SEO-ready website at an affordable initial cost — designed,
                  written and launched for you. Yours from day one, no lock-in.
                </p>
                <ul className="mt-6 space-y-3">
                  {["Premium design & build", "SEO foundation included", "Lead capture & WhatsApp built in", "Mobile-first & fast"].map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-obsidian/75">
                      <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-ember" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="h-full">
              <div className="flex h-full flex-col rounded-3xl bg-obsidian p-8 text-white sm:p-10">
                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">Step Two — Optional</span>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  Grow Continuously.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  A monthly growth subscription for businesses that want more Google visibility,
                  more traffic and more enquiries — compounding month after month.
                </p>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {["Monthly SEO improvements", "New blog articles", "Location landing pages", "Website updates", "New lead-gen sections", "Conversion optimization", "Speed optimization", "Growth recommendations"].map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/70">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ember" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid="growth-custom-plan-cta"
                  onClick={() => { track("cta_click", { location: "growth", cta: "custom_plan" }); openLeadPopup(); }}
                  className="group mt-8 flex w-fit items-center gap-2 rounded-full bg-ember px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
                >
                  Get a Custom Plan <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 06 LEAD ENGINE */}
      <section className="py-20 lg:py-32" data-testid="lead-engine-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="06" label="The Lead Engine" />
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Built to Turn Visitors <span className="text-white/35">Into Enquiries.</span>
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {LEAD_FEATURES.map((f) => (
              <Item key={f.h} className="rounded-2xl border border-white/10 bg-ink p-7 transition-colors hover:border-ember/30">
                <f.icon className="h-6 w-6 text-ember" />
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight">{f.h}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/50">{f.p}</p>
              </Item>
            ))}
          </Stagger>

          {/* WHO IT'S FOR */}
          <div className="mt-24">
            <Reveal>
              <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Built for Businesses That Want More Enquiries.
              </h3>
            </Reveal>
            <Stagger className="mt-8 flex flex-wrap gap-3">
              {INDUSTRIES.map((ind) => (
                <Item key={ind}>
                  <span
                    data-testid={`industry-chip-${ind.toLowerCase().replace(/[\s&/]+/g, "-")}`}
                    className="inline-block rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/70 transition-colors hover:border-ember/50 hover:text-white"
                  >
                    {ind}
                  </span>
                </Item>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* 07 RECENT WORK */}
      <section className="border-t border-line bg-ink py-20 lg:py-32" data-testid="work-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Chapter index="07" label="Recent Work" />
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
                  Websites That <span className="text-white/35">Work for a Living.</span>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <Link
                to="/projects/"
                data-testid="view-all-projects-link"
                className="group flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-ember"
              >
                View All Projects <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
          <Stagger className="mt-14 grid gap-6 lg:grid-cols-3">
            {PROJECTS.slice(0, 3).map((p) => (
              <Item key={p.slug}>
                <Link
                  to={`/projects/${p.slug}/`}
                  data-testid={`project-card-${p.slug}`}
                  className="group block overflow-hidden rounded-2xl border border-white/10 bg-obsidian"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={thumb(p.image)}
                      alt={`${p.name} — ${p.industry} website project`}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    {p.sample ? (
                      <span className="absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 backdrop-blur">
                        Sample Project
                      </span>
                    ) : (
                      <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-ember px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                        <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live Website
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember">{p.industry}</p>
                    <h3 className="mt-2 font-display text-xl font-bold tracking-tight">{p.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/50">{p.objective}</p>
                  </div>
                </Link>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* 08 ABOUT TEASER */}
      <section className="py-20 lg:py-32" data-testid="about-teaser-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <div className="max-w-3xl">
            <Chapter index="08" label="The Person Behind It" />
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
                Meet Grotech Web
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <blockquote className="mt-6 border-l-2 border-ember pl-6 font-display text-xl leading-snug text-white/85 sm:text-2xl">
                "A website should do more than exist online. It should clearly explain your
                business, build trust and make it easier for potential customers to contact you."
              </blockquote>
              <p className="mt-6 max-w-xl leading-relaxed text-white/50">
                Founder Somil Grover brings 15+ years of website building experience to every project — from
                affordable business websites to AI-powered websites and chatbots. Every build answers
                one question: will this bring the business more enquiries?
              </p>
              <Link
                to="/about/"
                data-testid="about-teaser-cta"
                className="group mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider transition-colors hover:border-ember hover:text-ember"
              >
                Explore Grotech Web <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 09 REVIEWS */}
      <Testimonials />

      {/* 10 FAQ */}
      <HomeFAQ />

      <FomoBand />

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-24 lg:py-36" data-testid="final-cta-section">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,69,0,0.12),transparent_60%)]" />
        <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-8">
          <Reveal>
            <h2 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Ready to Turn Your Website Into a <span className="text-ember">Lead Generation Tool?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-xl text-white/50">
              Tell me about your business — you'll get a website strategy designed around generating
              enquiries, not just looking good.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button
                data-testid="final-cta-discuss-btn"
                onClick={() => { track("cta_click", { location: "final", cta: "discuss" }); openLeadPopup(); }}
                className="group flex items-center gap-2 rounded-full bg-ember px-8 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
              >
                Discuss My Business <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={waLink(CONTEXT_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-cta-whatsapp-btn"
                onClick={() => track("whatsapp_click", { location: "final_cta" })}
                className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
              >
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </div>
            <p className="mt-6 font-mono text-xs tracking-[0.2em] text-white/35">{WA_NUMBER_DISPLAY}</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
